<template>
  <div
    class="cs-track"
    :class="{ 'cs-track--scroll': scrollable }"
    :style="{ '--cs-fs': (fontSize || 14) + 'px' }"
    ref="trackRef"
  >
    <!-- Sliding pill indicator -->
    <span class="cs-pill" :style="pillStyle" aria-hidden="true"></span>

    <button
      v-for="opt in options"
      :key="opt.value"
      class="cs-btn"
      :class="{ 'cs-btn--active': modelValue === opt.value }"
      @click="select(opt.value)"
    >
      {{ opt.label }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";

interface Option {
  value: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    options: Option[];
    fontSize?: number;
    scrollable?: boolean;
  }>(),
  {
    fontSize: 14,
    scrollable: false,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", v: string): void;
  (e: "change", v: string): void;
}>();

const trackRef = ref<HTMLElement | null>(null);
const pillStyle = ref({ width: "0px", transform: "translateX(0px)" });

let pillReady = false;
let trackResizeObserver: ResizeObserver | null = null;
let delayedSyncTimer: ReturnType<typeof setTimeout> | null = null;

const handleResize = () => {
  updatePill(false);
};

function hasValidSelection() {
  return props.options.some((o) => o.value === props.modelValue);
}

function ensureValidSelection() {
  if (!props.options.length) return;
  if (hasValidSelection()) return;
  emit("update:modelValue", props.options[0].value);
}

function updatePill(animated = true) {
  if (!trackRef.value) return;
  const idx = props.options.findIndex((o) => o.value === props.modelValue);
  if (idx < 0) return;
  const btns = trackRef.value.querySelectorAll<HTMLButtonElement>(".cs-btn");
  const btn = btns[idx];
  if (!btn) return;

  if (!pillReady) {
    // First render: snap instantly, then enable transitions
    pillStyle.value = {
      width: btn.offsetWidth + "px",
      transform: `translateX(${btn.offsetLeft}px)`,
    };
    requestAnimationFrame(() => {
      pillReady = true;
    });
  } else {
    pillStyle.value = {
      width: btn.offsetWidth + "px",
      transform: `translateX(${btn.offsetLeft}px)`,
    };
  }
}

async function syncPillAfterLayout() {
  await nextTick();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      updatePill(false);
    });
  });
}

function select(value: string) {
  emit("update:modelValue", value);
  emit("change", value);
}

watch(
  () => props.modelValue,
  async () => {
    ensureValidSelection();
    await syncPillAfterLayout();
  }
);

watch(
  () => props.options,
  async () => {
    ensureValidSelection();
    await syncPillAfterLayout();
  },
  { deep: true, immediate: true }
);

onMounted(async () => {
  ensureValidSelection();
  await syncPillAfterLayout();
  window.addEventListener("resize", handleResize);

  if (trackRef.value) {
    trackResizeObserver = new ResizeObserver(() => {
      updatePill(false);
    });
    trackResizeObserver.observe(trackRef.value);
  }

  // Final safety sync after initial paints/fonts.
  delayedSyncTimer = setTimeout(() => {
    updatePill(false);
  }, 80);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
  if (trackResizeObserver) {
    trackResizeObserver.disconnect();
    trackResizeObserver = null;
  }
  if (delayedSyncTimer) {
    clearTimeout(delayedSyncTimer);
    delayedSyncTimer = null;
  }
});
</script>

<style scoped>
.cs-track {
  position: relative;
  display: flex;
  background: linear-gradient(180deg, #f4f5f8 0%, #eceef3 100%);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  padding: 4px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    0 1px 2px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(8px);
}

.cs-track--scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.cs-track--scroll::-webkit-scrollbar {
  display: none;
}

.cs-pill {
  position: absolute;
  top: 4px;
  left: 0;
  height: calc(100% - 8px);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.12),
    0 6px 16px rgba(15, 23, 42, 0.08);
  pointer-events: none;
  /* Transition only fires after first render (pillReady flag) */
  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    width 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform, width;
}

.cs-btn {
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 9px 16px !important;
  font-weight: 600 !important;
  color: #4b5563;
  border-radius: 10px;
  /* Override parent :deep(button) which forces 16px/1.5 on all buttons */
  font-size: 14px !important;
  line-height: 1.4 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  transition: color 0.2s ease;
  user-select: none;
  -webkit-user-select: none;
}

.cs-btn--active {
  color: #0f172a;
  font-weight: 600;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
}

/* Scale-up micro-interaction on click */
.cs-btn:active {
  transform: scale(0.96);
  transition:
    color 0.2s ease,
    transform 0.1s ease;
}

@media (hover: hover) {
  .cs-btn:hover {
    color: #1f2937;
  }
}

.cs-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.28);
}

/* On narrow phones, stack segment buttons vertically */
@media (max-width: 540px) {
  .cs-track {
    display: block;
    padding: 8px;
    border-radius: 16px;
  }

  .cs-pill {
    display: none;
  }

  .cs-btn {
    display: block;
    width: calc(100% - 12px);
    margin-left: 6px;
    margin-right: 6px;
    flex: none;
    white-space: normal;
    text-overflow: clip;
    line-height: 1.35 !important;
    padding: 10px 12px !important;
  }

  .cs-btn + .cs-btn {
    margin-top: 4px;
  }

  .cs-btn--active {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid rgba(148, 163, 184, 0.25);
    box-shadow:
      0 1px 2px rgba(15, 23, 42, 0.12),
      0 6px 16px rgba(15, 23, 42, 0.08);
  }
}
</style>
